/**
 * MetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitPage — KPI dashboard
 * ported from legacy apps/dash-legacy/src/routes/_authed/reports/roas.tsx.
 *
 * Renders 10 performance KPI tiles in a grid (CPC, CPA, EPC, Avg Sale, CR,
 * ROI, ROAS, Revenue, Payout, Profit). Each tile shows current value, inline
 * emerald SVG sparkline, and (when compare is on) a vs-previous delta.
 * Click a tile -> opens an inline breakdown panel showing per-campaign or
 * per-affiliate values for that single metric. Date-range picker + compare
 * toggle drive the query params.
 *
 * Plain Tailwind only. Light theme. No external ui lib imports. Raw fetch is
 * confined to ./api — this page consumes hooks only.
 */
import { useMemo, useState } from "react";
import {
	useMetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitBreakdown,
	useMetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitList,
} from "./hooks";
import type { MetricKey, MetricsKpi, MetricsSparkBucket } from "./api";

// ────────────────────────────── formatting ───────────────────────────────
function fmtMoney(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return n.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
}

function fmtMoneyPrecise(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return n.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function fmtNumber(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return n.toLocaleString("en-US");
}

function fmtPercent(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return `${(n * 100).toFixed(2)}%`;
}

function fmtRatio(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return `${n.toFixed(2)}×`;
}

function formatValue(kpi: MetricsKpi): string {
	switch (kpi.format) {
		case "money":
			return fmtMoney(kpi.value);
		case "money_precise":
			return fmtMoneyPrecise(kpi.value);
		case "percent":
			return fmtPercent(kpi.value);
		case "ratio":
			return fmtRatio(kpi.value);
		default:
			return fmtNumber(kpi.value);
	}
}

function formatBreakdownValue(v: number, format: MetricsKpi["format"]): string {
	switch (format) {
		case "money":
			return fmtMoney(v);
		case "money_precise":
			return fmtMoneyPrecise(v);
		case "percent":
			return fmtPercent(v);
		case "ratio":
			return fmtRatio(v);
		default:
			return fmtNumber(v);
	}
}

function deltaPct(curr: number, prev: number): number | null {
	if (!Number.isFinite(curr) || !Number.isFinite(prev)) return null;
	if (prev === 0) return curr === 0 ? 0 : null;
	return (curr - prev) / Math.abs(prev);
}

function fmtDelta(pct: number | null): string {
	if (pct === null) return "—";
	const sign = pct > 0 ? "+" : "";
	return `${sign}${(pct * 100).toFixed(1)}%`;
}

// ────────────────────────────── date helpers ─────────────────────────────
function toDateInput(unixSec: number): string {
	const d = new Date(unixSec * 1000);
	const yyyy = d.getUTCFullYear();
	const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
	const dd = String(d.getUTCDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

function fromDateInput(value: string, endOfDay = false): number {
	const parts = value.split("-").map((n) => parseInt(n, 10));
	const y = parts[0] ?? 1970;
	const m = parts[1] ?? 1;
	const d = parts[2] ?? 1;
	const ms = Date.UTC(
		y,
		m - 1,
		d,
		endOfDay ? 23 : 0,
		endOfDay ? 59 : 0,
		endOfDay ? 59 : 0,
	);
	return Math.floor(ms / 1000);
}

// ──────────────────────── inline SVG sparkline ───────────────────────────
function Sparkline({ points }: { points: MetricsSparkBucket[] }) {
	const W = 120;
	const H = 32;
	const PAD = 2;

	if (!points || points.length === 0) {
		return (
			<svg viewBox={`0 0 ${W} ${H}`} className="h-8 w-full" aria-hidden="true">
				<line
					x1={PAD}
					y1={H / 2}
					x2={W - PAD}
					y2={H / 2}
					stroke="#d4d4d8"
					strokeWidth="1"
					strokeDasharray="2 2"
				/>
			</svg>
		);
	}

	const values = points.map((p) => p.value);
	let min = Math.min(...values);
	let max = Math.max(...values);
	if (min === max) {
		min -= 1;
		max += 1;
	}
	const span = max - min;
	const step = points.length > 1 ? (W - 2 * PAD) / (points.length - 1) : 0;

	const coords = points.map((p, i) => {
		const x = PAD + i * step;
		const y = H - PAD - ((p.value - min) / span) * (H - 2 * PAD);
		return `${x.toFixed(1)},${y.toFixed(1)}`;
	});

	const last = coords[coords.length - 1]?.split(",") ?? [];
	const lx = parseFloat(last[0] ?? "0");
	const ly = parseFloat(last[1] ?? "0");

	return (
		<svg viewBox={`0 0 ${W} ${H}`} className="h-8 w-full" aria-hidden="true">
			<polyline
				points={coords.join(" ")}
				fill="none"
				stroke="#10b981"
				strokeWidth="1.5"
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
			<circle cx={lx} cy={ly} r="2" fill="#10b981" />
		</svg>
	);
}

// ────────────────────────────── KPI tile ─────────────────────────────────
function KpiTile({
	kpi,
	compare,
	active,
	onClick,
}: {
	kpi: MetricsKpi;
	compare: boolean;
	active: boolean;
	onClick: () => void;
}) {
	const d = compare ? deltaPct(kpi.value, kpi.previous) : null;
	const invert = kpi.key === "cpc" || kpi.key === "cpa";
	let deltaClass = "text-zinc-400";
	if (d !== null && Math.abs(d) >= 0.0005) {
		const positive = invert ? d < 0 : d > 0;
		deltaClass = positive ? "text-emerald-600" : "text-red-600";
	}

	const borderClass = active
		? "border-emerald-500 ring-2 ring-emerald-100"
		: "border-zinc-200 hover:border-zinc-300";

	return (
		<button
			type="button"
			onClick={onClick}
			data-testid={`metrics-kpi-tile-${kpi.key}`}
			aria-pressed={active}
			className={`flex flex-col gap-2 rounded-lg border ${borderClass} bg-white p-4 text-left shadow-sm transition-all`}
		>
			<div className="flex items-start justify-between">
				<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
					{kpi.label}
				</span>
				{compare && (
					<span
						className={`text-xs font-medium tabular-nums ${deltaClass}`}
						data-testid={`metrics-kpi-delta-${kpi.key}`}
					>
						{fmtDelta(d)}
					</span>
				)}
			</div>
			<div
				className="text-2xl font-semibold text-zinc-900 tabular-nums"
				data-testid={`metrics-kpi-value-${kpi.key}`}
			>
				{formatValue(kpi)}
			</div>
			<Sparkline points={kpi.spark} />
		</button>
	);
}

// ─────────────────────── breakdown drill-through ────────────────────────
function BreakdownPanel({
	metric,
	label,
	format,
	group,
	fromSec,
	toSec,
	compare,
	onClose,
	onChangeGroup,
}: {
	metric: MetricKey;
	label: string;
	format: MetricsKpi["format"];
	group: "campaign" | "affiliate";
	fromSec: number;
	toSec: number;
	compare: boolean;
	onClose: () => void;
	onChangeGroup: (g: "campaign" | "affiliate") => void;
}) {
	const breakdownQuery = useMetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitBreakdown(
		metric,
		{ from: fromSec, to: toSec, compare, group },
	);

	const rows = breakdownQuery.data?.rows ?? [];
	return (
		<section
			data-testid="metrics-breakdown-panel"
			className="flex flex-col gap-4 rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
		>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-baseline gap-2">
					<h2 className="text-lg font-semibold text-zinc-900">{label} breakdown</h2>
					<span className="text-xs text-zinc-500">
						by {group === "campaign" ? "campaign" : "affiliate"}
					</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white p-0.5">
						{(["campaign", "affiliate"] as const).map((g) => (
							<button
								key={g}
								type="button"
								onClick={() => onChangeGroup(g)}
								data-testid={`metrics-breakdown-group-${g}`}
								className={`h-7 rounded px-2 text-xs font-medium capitalize transition-colors ${
									group === g
										? "bg-emerald-600 text-white"
										: "text-zinc-600 hover:bg-zinc-100"
								}`}
							>
								{g}
							</button>
						))}
					</div>
					<button
						type="button"
						onClick={onClose}
						data-testid="metrics-breakdown-close"
						className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
					>
						Close
					</button>
				</div>
			</div>

			{breakdownQuery.isLoading ? (
				<p
					className="text-sm text-zinc-500"
					data-testid="metrics-breakdown-loading"
				>
					Loading.
				</p>
			) : breakdownQuery.error ? (
				<p className="text-sm text-red-600" role="alert">
					{breakdownQuery.error.message}
				</p>
			) : rows.length === 0 ? (
				<p className="text-sm text-zinc-500" data-testid="metrics-breakdown-empty">
					No data in this range.
				</p>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse"
						data-testid="metrics-breakdown-table"
					>
						<thead className="bg-zinc-50">
							<tr>
								<th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
									{group === "campaign" ? "Campaign" : "Affiliate"}
								</th>
								<th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
									{label}
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{rows.map((r) => (
								<tr
									key={r.id}
									data-testid={`metrics-breakdown-row-${r.id}`}
									className="hover:bg-zinc-50/60"
								>
									<td className="max-w-md truncate px-3 py-2 text-sm font-medium text-zinc-900">
										{r.name}
									</td>
									<td className="px-3 py-2 text-right text-sm tabular-nums text-zinc-800">
										{formatBreakdownValue(r.value, format)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

// ────────────────────────────── default KPIs ─────────────────────────────
const METRIC_DEFAULTS: Array<{ key: MetricKey; label: string; format: MetricsKpi["format"] }> = [
	{ key: "cpc", label: "CPC", format: "money_precise" },
	{ key: "cpa", label: "CPA", format: "money_precise" },
	{ key: "epc", label: "EPC", format: "money_precise" },
	{ key: "avg_sale", label: "Avg Sale", format: "money_precise" },
	{ key: "cr", label: "CR", format: "percent" },
	{ key: "roi", label: "ROI", format: "percent" },
	{ key: "roas", label: "ROAS", format: "ratio" },
	{ key: "revenue", label: "Revenue", format: "money" },
	{ key: "payout", label: "Payout", format: "money" },
	{ key: "profit", label: "Profit", format: "money" },
];

// ────────────────────────────── Page ─────────────────────────────────────
export function MetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitPage() {
	const now = Math.floor(Date.now() / 1000);
	const defaultFrom = now - 7 * 86400;

	const [dateFrom, setDateFrom] = useState<string>(toDateInput(defaultFrom));
	const [dateTo, setDateTo] = useState<string>(toDateInput(now));
	const [compare, setCompare] = useState<boolean>(true);
	const [activeMetric, setActiveMetric] = useState<MetricKey | null>(null);
	const [group, setGroup] = useState<"campaign" | "affiliate">("campaign");

	const fromSec = useMemo(() => fromDateInput(dateFrom, false), [dateFrom]);
	const toSec = useMemo(() => fromDateInput(dateTo, true), [dateTo]);

	const listQuery = useMetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitList({
		from: fromSec,
		to: toSec,
		compare,
	});

	// Merge server kpis with default ordering/labels so the 10-tile grid is
	// always stable even if the server response is partial.
	const kpis: MetricsKpi[] = useMemo(() => {
		const byKey = new Map<MetricKey, MetricsKpi>();
		for (const k of listQuery.data?.kpis ?? []) byKey.set(k.key, k);
		return METRIC_DEFAULTS.map((m) => {
			const found = byKey.get(m.key);
			if (found) return { ...m, ...found, label: m.label, format: m.format };
			return {
				key: m.key,
				label: m.label,
				format: m.format,
				value: Number.NaN,
				previous: Number.NaN,
				spark: [],
			};
		});
	}, [listQuery.data]);

	const activeKpi = useMemo(
		() => (activeMetric ? kpis.find((k) => k.key === activeMetric) ?? null : null),
		[activeMetric, kpis],
	);

	function setQuickRange(days: number) {
		setDateFrom(toDateInput(now - days * 86400));
		setDateTo(toDateInput(now));
	}

	return (
		<main
			data-testid="metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-10"
		>
			{/* Header */}
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Performance report
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Revenue &amp; ROAS metrics
				</h1>
				<p className="text-sm text-zinc-500">
					CPC, CPA, EPC, avg sale, conversion rate, ROI, ROAS, revenue, payout,
					profit. Click any tile to drill into per-campaign or per-affiliate
					breakdown.
				</p>
			</header>

			{/* Filters */}
			<section
				className="flex flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm"
				aria-label="Metrics filters"
			>
				<div className="flex flex-col gap-1">
					<label
						className="text-xs font-medium text-zinc-500"
						htmlFor="metrics-from"
					>
						From
					</label>
					<input
						id="metrics-from"
						data-testid="metrics-date-from"
						type="date"
						value={dateFrom}
						onChange={(e) => setDateFrom(e.target.value)}
						className="h-9 w-40 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label
						className="text-xs font-medium text-zinc-500"
						htmlFor="metrics-to"
					>
						To
					</label>
					<input
						id="metrics-to"
						data-testid="metrics-date-to"
						type="date"
						value={dateTo}
						onChange={(e) => setDateTo(e.target.value)}
						className="h-9 w-40 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
					/>
				</div>

				<div className="flex flex-col gap-1">
					<span className="text-xs font-medium text-zinc-500">Quick range</span>
					<div className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white p-0.5">
						{[1, 7, 14, 30, 90].map((d) => (
							<button
								key={d}
								type="button"
								onClick={() => setQuickRange(d)}
								data-testid={`metrics-quick-${d}`}
								className="h-8 rounded px-3 text-xs font-medium text-zinc-600 hover:bg-zinc-100"
							>
								{d}d
							</button>
						))}
					</div>
				</div>

				<label className="flex items-center gap-2 text-sm text-zinc-700">
					<input
						type="checkbox"
						data-testid="metrics-compare-toggle"
						checked={compare}
						onChange={(e) => setCompare(e.target.checked)}
						className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
					/>
					Compare to previous period
				</label>

				<div className="ml-auto flex items-center gap-2 text-xs text-zinc-500">
					<span
						className={`inline-flex h-2 w-2 rounded-full ${
							listQuery.isFetching ? "animate-pulse bg-emerald-500" : "bg-emerald-400"
						}`}
						aria-hidden="true"
					/>
					<span data-testid="metrics-status">
						{listQuery.isFetching ? "Refreshing" : "Auto-refresh 30s"}
					</span>
				</div>
			</section>

			{/* Error banner */}
			{listQuery.error && (
				<div
					role="alert"
					data-testid="metrics-error"
					className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
				>
					{listQuery.error.message}
				</div>
			)}

			{/* KPI grid */}
			<section
				aria-label="Performance KPIs"
				className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
				data-testid="metrics-kpi-grid"
			>
				{listQuery.isLoading ? (
					<p
						className="col-span-full text-sm text-zinc-500"
						data-testid="metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit-loading"
					>
						Loading.
					</p>
				) : kpis.every((k) => !Number.isFinite(k.value)) ? (
					<p
						className="col-span-full text-sm text-zinc-500"
						data-testid="metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit-empty"
					>
						No data.
					</p>
				) : (
					kpis.map((kpi) => (
						<KpiTile
							key={kpi.key}
							kpi={kpi}
							compare={compare}
							active={activeMetric === kpi.key}
							onClick={() => setActiveMetric(kpi.key === activeMetric ? null : kpi.key)}
						/>
					))
				)}
			</section>

			{/* Drill-through */}
			{activeKpi && (
				<BreakdownPanel
					metric={activeKpi.key}
					label={activeKpi.label}
					format={activeKpi.format}
					group={group}
					fromSec={fromSec}
					toSec={toSec}
					compare={compare}
					onClose={() => setActiveMetric(null)}
					onChangeGroup={setGroup}
				/>
			)}
		</main>
	);
}

export default MetricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitPage;
